import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, Select, Stack, TextField } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { formatDateRange } from 'helpers/helpers.js';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TimeInMS } from 'types/time';
import GenericModal from 'components/GenericModal';

const DATE_TO_DATE_LABEL = 'date to date';

const formatSelectedOptionLabel = option => {
  if (!option) return '';
  if (option.label !== DATE_TO_DATE_LABEL) return option.label ?? '';

  if (typeof option.value !== 'string') return option.value ?? '';

  const [startValue, endValue] = option.value.split('_');
  const startDate = DateTime.fromFormat(startValue ?? '', 'yyyy-MM-dd');
  const endDate = DateTime.fromFormat(endValue ?? '', 'yyyy-MM-dd');

  if (!startDate.isValid || !endDate.isValid) return option.value;

  return formatDateRange(startDate, endDate);
};

/**
 * A button that opens a dialog containing a select (dropdown)
 * @param {*} props
 * @returns
 */
export default function DialogSelect(props) {
  const [open, setOpen] = React.useState(false);
  const [selectedStartDate, setSelectedStartDate] = React.useState(() => DateTime.now().startOf('day'));
  const [selectedEndDate, setSelectedEndDate] = React.useState(() => DateTime.now().startOf('day'));
  const [otherTimeFrame, setOtherTimeFrame] = React.useState(false);
  const [tempSelectedOption, setTempSelectedOption] = React.useState(props.rangeObj || { label: 'Today', value: 86400000 });
  const [finalSelectedOption, setFinalSelectedOption] = React.useState(props.rangeObj || { label: 'Today', value: 86400000 });

  const firstUpdate = useSelector(state => state.sensors.data.firstUpdate);
  const minDate = DateTime.fromSeconds(firstUpdate);
  const maxDate = DateTime.now();
  const maxDateRangeDays = Number.isFinite(props.maxDateRangeDays) ? props.maxDateRangeDays : null;
  const maxEndDate =
    maxDateRangeDays && selectedStartDate
      ? DateTime.min(maxDate, selectedStartDate.startOf('day').plus({ days: maxDateRangeDays - 1 }))
      : maxDate;
  const timeRangesListRef = useRef(null);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!props.rangeObj) {
      return;
    }

    setTempSelectedOption(
      props.rangeObj.label === DATE_TO_DATE_LABEL ? { label: DATE_TO_DATE_LABEL, value: DATE_TO_DATE_LABEL } : props.rangeObj,
    );
    setFinalSelectedOption(props.rangeObj);
    setOtherTimeFrame(props.rangeObj.label === DATE_TO_DATE_LABEL);
  }, [props.rangeObj]);

  /**
   * Set the selected option
   * @param {*} event
   */
  const handleChange = event => {
    let selected = event.target.value;

    let timeRangeobj;
    if (selected !== DATE_TO_DATE_LABEL) {
      setOtherTimeFrame(false);
      timeRangeobj = props.selectOptions.find(option => option.value === Number(selected));
      setTempSelectedOption(timeRangeobj);
    } else {
      setOtherTimeFrame(true);
      setTempSelectedOption({ label: DATE_TO_DATE_LABEL, value: DATE_TO_DATE_LABEL });
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleStartDateChange = date => {
    setSelectedStartDate(date);
    if (date && selectedEndDate) {
      const latestEndDate = maxDateRangeDays
        ? DateTime.min(maxDate, date.startOf('day').plus({ days: maxDateRangeDays - 1 }))
        : maxDate;
      if (date > selectedEndDate) {
        setSelectedEndDate(date);
      } else if (selectedEndDate > latestEndDate) {
        setSelectedEndDate(latestEndDate);
      }
    }
  };

  const handleEndDateChange = date => {
    if (date && selectedStartDate && date < selectedStartDate) {
      setSelectedEndDate(selectedStartDate);
      return;
    }
    setSelectedEndDate(date && date > maxEndDate ? maxEndDate : date);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOK = async reason => {
    setOpen(false);
    if (reason !== 'backdropClick') {
      if (tempSelectedOption.label === DATE_TO_DATE_LABEL && (selectedStartDate === null || selectedEndDate === null)) {
        alert('please select date range');
        return;
      }
      if (
        tempSelectedOption.label === DATE_TO_DATE_LABEL &&
        maxDateRangeDays &&
        selectedEndDate.diff(selectedStartDate, 'days').days > maxDateRangeDays - 1
      ) {
        alert(`Please choose a date range of up to ${maxDateRangeDays} days.`);
        return;
      }
      if (tempSelectedOption.label === DATE_TO_DATE_LABEL && (selectedStartDate !== null || selectedEndDate !== null)) {
        const selectedDate = {
          label: DATE_TO_DATE_LABEL,
          value: `${selectedStartDate.toFormat('yyyy-MM-dd')}_${selectedEndDate.toFormat('yyyy-MM-dd')}`,
        };
        dispatch(props.onSelectionOK(selectedDate));
        setFinalSelectedOption({
          label: DATE_TO_DATE_LABEL,
          value: `${formatDateRange(selectedStartDate, selectedEndDate)}`,
        });
        return;
      } else {
        setFinalSelectedOption({
          label: tempSelectedOption.label,
          value: tempSelectedOption.value,
        });
      }
    }

    dispatch(props.onSelectionOK(tempSelectedOption));
    // props.onClose();
    // props.setTimeRange(tempSelectedOption);
  };

  const getOptions = () => {
    return props.selectOptions?.map(option => {
      return (
        <option value={option.value ?? 0} key={option.value}>
          {option.label ?? ''}
        </option>
      );
    });
  };

  const dialogTitleNode = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{props.dialogTitle ?? ''}</Box>
      {props.toolTip && (
        <Tooltip
          title={props.toolTip}
          slotProps={{
            popper: {
              sx: { zIndex: 50020 },
            },
          }}>
          <IconButton size="small" sx={{ color: 'grey', p: 0.5 }}>
            <InfoIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const DialogSelectContent = () => (
    <>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}>
        <FormControl sx={{ m: 1, minWidth: 120, flex: 1 }}>
          <InputLabel>{props.selectLabel ?? ''}</InputLabel>
          <Select
            ref={timeRangesListRef}
            native
            value={tempSelectedOption.value ?? ''}
            onChange={handleChange}
            input={<OutlinedInput label={props.selectLabel ?? ''} />}>
            {getOptions()}
          </Select>
        </FormControl>
        <IconButton
          aria-label="ok"
          onClick={handleOK}
          sx={{
            mt: 1,
            '&:hover': {
              backgroundColor: 'rgba(225, 225, 225, 1)',
              color: 'black',
            },
          }}>
          <DoneIcon />
        </IconButton>
      </Box>
      {otherTimeFrame && (
        <Stack>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              sx={{
                margin: 1,
                width: 265,
              }}
              label="Start date"
              value={selectedStartDate}
              onChange={handleStartDateChange}
              minDate={minDate}
              maxDate={maxDate}
              slots={{ textField: TextField }}
              slotProps={{
                textField: { variant: 'outlined' },
                popper: { sx: { zIndex: 50010 } },
              }}
              format="dd/MM/yyyy"
            />
            <DatePicker
              sx={{
                margin: 1,
                width: 265,
              }}
              label="End date"
              value={selectedEndDate}
              onChange={handleEndDateChange}
              minDate={selectedStartDate || minDate}
              maxDate={maxEndDate}
              slots={{ textField: TextField }}
              slotProps={{
                textField: { variant: 'outlined' },
                popper: { sx: { zIndex: 50010 } },
              }}
              format="dd/MM/yyyy"
            />
          </LocalizationProvider>
        </Stack>
      )}
    </>
  );

  return (
    <div>
      <MDButton color={props.color || 'white'} variant="text" onClick={handleClickOpen} sx={{ p: { xs: 0, sm: '.5rem' } }}>
        <Stack>
          <MDTypography variant="button" fontWeight="bold" color={props.color || 'white'} sx={{ color: props.color }}>
            {props.buttonTitle ?? ''}
          </MDTypography>
          <MDTypography variant="caption" fontWeight="light" color={props.color || 'white'} sx={{ color: props.color }}>
            {formatSelectedOptionLabel(finalSelectedOption)}
          </MDTypography>
        </Stack>
      </MDButton>
      <GenericModal
        open={open}
        onClose={handleClose}
        title={dialogTitleNode}
        Component={DialogSelectContent}
        contentProps={{
          dividers: true,
          sx: {
            p: 2,
            borderBottom: 'none !important',
          },
        }}
        closeOnBackdrop={true}
        closeOnEsc={true}
      />
    </div>
  );
}
